import { debounce, isEqual } from "lodash";
import * as React from "react";
import { RefObject } from "react";
import { adjustPosition, AdjustPositionOptions } from "../../EasyProjectsCommon/General";
import { memoizeOne } from "../../EasyProjectsCommon/memoizeOne";
import { ResizeEntry, ResizeSensor } from "../../sharedComponents/resizeSensor/ResizeSensor";
import { domUtils } from "../../utils/domUtils";

export type AdjustContainerProps = {
    adjustOptions: AdjustPositionOptions;
    baseElRef: RefObject<HTMLElement>;
    childElRef: RefObject<HTMLElement>;
    trackTrigger?: boolean;
    scrollIntoView?: boolean;
    /**
     * Updates position when popup size is changed
     */
    trackContent?: boolean;
    /**
     * Updates content position on scroll.
     * Used for popups rendered to body.
     */
    trackScroll?: boolean;
};

export class AdjustPosition extends React.Component<AdjustContainerProps> {
    triggerRect: any = {};
    isUnmounted: boolean;
    private _scrollIntoViewOnce = memoizeOne(this.scrollIntoView);
    private _displayOrigin: string;
    private _focusedElement: HTMLElement;

    componentDidMount() {
        this.triggerAdjust();

        if (this.props.trackScroll) {
            window.addEventListener("scroll", this.onParentPositionChanged, true);
        }
    }

    isContentHidden = () => this.props.childElRef.current.style.display === "none";

    hideContent = () => {
        if (this.isContentHidden()) {
            return;
        }

        this._focusedElement = document.activeElement as HTMLElement;
        this._displayOrigin = this.props.childElRef.current.style.display;

        this.props.childElRef.current.style.display = "none";
    };

    showContent = () => {
        if (!this.isContentHidden()) {
            return;
        }

        this.props.childElRef.current.style.display = this._displayOrigin;

        if (this.props.childElRef.current.contains(this._focusedElement)) {
            this._focusedElement.focus();
        }

        this._focusedElement = null;
    };

    onParentPositionChanged = debounce(() => {
        if (this.isUnmounted) {
            return;
        }

        const isTriggerVisible = domUtils.isVisible(this.props.baseElRef.current);

        if (isTriggerVisible) {
            this.showContent();
            this.adjust();
        } else {
            this.hideContent();
        }
    }, 20);

    canAdjust = () => {
        return this.props.baseElRef.current && this.props.childElRef.current;
    };

    triggerAdjust = () => {
        if (this.props.trackTrigger) {
            this.recursiveAdjust();
        } else {
            requestAnimationFrame(() => {
                if (this.canAdjust()) {
                    this.adjust();
                }
            });
        }
    };

    adjust = () => {
        adjustPosition(this.props.baseElRef.current, this.props.childElRef.current, this.props.adjustOptions);
        this._scrollIntoViewOnce();
    };

    getRectObj = () => {
        const rect = this.props.baseElRef.current.getBoundingClientRect();
        const { height, width, top, left } = rect; // dimensions to track

        return {
            height,
            width,
            top,
            left,
        };
    };

    recursiveAdjust = () => {
        if (this.isUnmounted) {
            return;
        }
        if (this.canAdjust()) {
            const rect = this.getRectObj();

            if (!isEqual(this.triggerRect, rect)) {
                this.triggerRect = rect;
                this.adjust();
            }

            requestAnimationFrame(() => {
                this.recursiveAdjust();
            });
        }
    };

    scrollIntoView() {
        if (this.props.scrollIntoView) {
            const popup = this.props.childElRef.current as HTMLElement;
            // don't try to use smooth scrolling because it doesn't work when
            // virt list (with AutoSizer) is rendered inside popup
            // sample -> notification recipients with a lot of selected users..
            domUtils.scrollIntoViewIfNeeded(popup, { behavior: "auto", block: "nearest" });
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;

        window.removeEventListener("scroll", this.onParentPositionChanged, true);
    }

    private _maxContentRect: DOMRectReadOnly;
    onResize = (entries: ResizeEntry[]) => {
        const entry = entries[0];
        const contentRect = entry.contentRect;

        if (!this._maxContentRect) {
            this._maxContentRect = contentRect;
            return;
        }

        // TODO: it's better to run adjusting only if it's really needed (popup is visible partially)
        if (this._maxContentRect.width < contentRect.width || this._maxContentRect.height < contentRect.height) {
            if (this.canAdjust()) {
                this.adjust();
            }
        }

        this._maxContentRect = {
            ...contentRect,
            width: Math.max(this._maxContentRect.width, contentRect.width),
            height: Math.max(this._maxContentRect.height, contentRect.height),
        };
    };

    render() {
        if (this.props.trackContent) {
            return <ResizeSensor onResize={this.onResize}>{this.props.children}</ResizeSensor>;
        }

        return this.props.children;
    }
}
